import type { Language } from '../i18n';

const STORAGE_KEY = 'gardenHub.language';

export type PdfLabels = {
	generatedOn: (date: string) => string;
	tryHortEra: string;
	planOverview: string;
	style: string;
	gardenSize: string;
	location: string;
	description: string;
	beforeAfter: string;
	currentState: string;
	designVision: string;
	conceptualPlan: string;
	keyFeatures: string;
	projectPlan: string;
	implementationInstructions: string;
	implementationSteps: string;
	requiredTools: string;
	materials: string;
	materialLine: (quantity: number, unit: string, unitPrice: number, unitLabel: string) => string;
	detailedPlan: string;
	estimatedPricing: string;
	estimatedMid: string;
	labor: string;
	timeline: string;
	ganttTimelineDraft: string;
	pageOf: (page: number, total: number) => string;
	weeks: string;
	task: string;
	offerTitleSuffix: string;
	offerVision: string;
	offerDetails: string;
	offeredPrice: string;
	validUntil: string;
	projectStart: string;
	timelineEstimate: string;
	message: string;
	ganttTimelineFromOffer: string;
	projectOverview: string;
	status: string;
	budget: string;
	dueDate: string;
	client: string;
	company: string;
	offerPrice: string;
	gardenVision: string;
	proposedActualVision: string;
	projectGantt: string;
	taskList: string;
	moreTasks: (count: number) => string;
	assignees: string;
	assigneesUnassigned: string;
	statusLabel: string;
	dependencies: string;
	dependenciesNone: string;
	progress: string;
	dateLocale: string;
};

const labelsByLanguage: Record<Language, PdfLabels> = {
	en: {
		generatedOn: (date) => `Generated on ${date}`,
		tryHortEra: 'Try HortEra: hortera.org',
		planOverview: 'Plan Overview',
		style: 'Style:',
		gardenSize: 'Garden Size:',
		location: 'Location:',
		description: 'Description:',
		beforeAfter: 'Before & After',
		currentState: 'Current State',
		designVision: 'Design Vision',
		conceptualPlan: 'Conceptual Plan',
		keyFeatures: 'Key Features',
		projectPlan: 'Project Plan',
		implementationInstructions: 'Implementation Instructions',
		implementationSteps: 'Implementation Steps',
		requiredTools: 'Required Tools',
		materials: 'Materials',
		materialLine: (quantity, unit, unitPrice, unitLabel) =>
			`${quantity} ${unit} × ${unitPrice.toLocaleString('en-GB')} EUR/${unitLabel}`,
		detailedPlan: 'Detailed Plan',
		estimatedPricing: 'Estimated Pricing',
		estimatedMid: 'Estimated (mid):',
		labor: 'Labor',
		timeline: 'Timeline',
		ganttTimelineDraft: 'Gantt Timeline (Draft)',
		pageOf: (page, total) => `Page ${page} of ${total}`,
		weeks: 'Weeks',
		task: 'Task',
		offerTitleSuffix: 'Offer',
		offerVision: 'Offer Vision',
		offerDetails: 'Offer Details',
		offeredPrice: 'Offered Price:',
		validUntil: 'Valid until:',
		projectStart: 'Project start:',
		timelineEstimate: 'Timeline estimate:',
		message: 'Message:',
		ganttTimelineFromOffer: 'Gantt Timeline (From Offer)',
		projectOverview: 'Project Overview',
		status: 'Status:',
		budget: 'Budget:',
		dueDate: 'Due date:',
		client: 'Client:',
		company: 'Company:',
		offerPrice: 'Offer price:',
		gardenVision: 'Garden Vision',
		proposedActualVision: 'Proposed / Actual Vision',
		projectGantt: 'Project Gantt',
		taskList: 'Task List',
		moreTasks: (count) => `(+${count} more tasks)`,
		assignees: 'Assignees:',
		assigneesUnassigned: 'Assignees: Unassigned',
		statusLabel: 'Status:',
		dependencies: 'Dependencies:',
		dependenciesNone: 'none',
		progress: 'Progress:',
		dateLocale: 'en-GB'
	},
	de: {
		generatedOn: (date) => `Erstellt am ${date}`,
		tryHortEra: 'HortEra ausprobieren: hortera.org',
		planOverview: 'Planübersicht',
		style: 'Stil:',
		gardenSize: 'Gartengröße:',
		location: 'Standort:',
		description: 'Beschreibung:',
		beforeAfter: 'Vorher & Nachher',
		currentState: 'Aktueller Zustand',
		designVision: 'Designvision',
		conceptualPlan: 'Konzeptplan',
		keyFeatures: 'Hauptmerkmale',
		projectPlan: 'Projektplan',
		implementationInstructions: 'Umsetzungsanweisungen',
		implementationSteps: 'Umsetzungsschritte',
		requiredTools: 'Erforderliche Werkzeuge',
		materials: 'Materialien',
		materialLine: (quantity, unit, unitPrice, unitLabel) =>
			`${quantity} ${unit} × ${unitPrice.toLocaleString('de-DE')} EUR/${unitLabel}`,
		detailedPlan: 'Detaillierter Plan',
		estimatedPricing: 'Geschätzte Kosten',
		estimatedMid: 'Geschätzt (Mittelwert):',
		labor: 'Arbeit',
		timeline: 'Zeitplan',
		ganttTimelineDraft: 'Gantt-Zeitplan (Entwurf)',
		pageOf: (page, total) => `Seite ${page} von ${total}`,
		weeks: 'Wochen',
		task: 'Aufgabe',
		offerTitleSuffix: 'Angebot',
		offerVision: 'Angebotsvision',
		offerDetails: 'Angebotsdetails',
		offeredPrice: 'Angebotspreis:',
		validUntil: 'Gültig bis:',
		projectStart: 'Projektstart:',
		timelineEstimate: 'Zeitschätzung:',
		message: 'Nachricht:',
		ganttTimelineFromOffer: 'Gantt-Zeitplan (aus Angebot)',
		projectOverview: 'Projektübersicht',
		status: 'Status:',
		budget: 'Budget:',
		dueDate: 'Fällig am:',
		client: 'Kunde:',
		company: 'Unternehmen:',
		offerPrice: 'Angebotspreis:',
		gardenVision: 'Gartenvision',
		proposedActualVision: 'Vorgeschlagene / aktuelle Vision',
		projectGantt: 'Projekt-Gantt',
		taskList: 'Aufgabenliste',
		moreTasks: (count) => `(+${count} weitere Aufgaben)`,
		assignees: 'Zugewiesen:',
		assigneesUnassigned: 'Zugewiesen: Nicht zugewiesen',
		statusLabel: 'Status:',
		dependencies: 'Abhängigkeiten:',
		dependenciesNone: 'keine',
		progress: 'Fortschritt:',
		dateLocale: 'de-DE'
	},
	hr: {
		generatedOn: (date) => `Generirano ${date}`,
		tryHortEra: 'Isprobajte HortEra: hortera.org',
		planOverview: 'Pregled plana',
		style: 'Stil:',
		gardenSize: 'Veličina vrta:',
		location: 'Lokacija:',
		description: 'Opis:',
		beforeAfter: 'Prije i poslije',
		currentState: 'Trenutačno stanje',
		designVision: 'Dizajnerska vizija',
		conceptualPlan: 'Konceptualni plan',
		keyFeatures: 'Ključne značajke',
		projectPlan: 'Projektni plan',
		implementationInstructions: 'Upute za izvedbu',
		implementationSteps: 'Koraci izvedbe',
		requiredTools: 'Potrebni alati',
		materials: 'Materijali',
		materialLine: (quantity, unit, unitPrice, unitLabel) =>
			`${quantity} ${unit} × ${unitPrice.toLocaleString('hr-HR')} EUR/${unitLabel}`,
		detailedPlan: 'Detaljni plan',
		estimatedPricing: 'Procijenjena cijena',
		estimatedMid: 'Procijenjeno (srednje):',
		labor: 'Rad',
		timeline: 'Vremenski plan',
		ganttTimelineDraft: 'Gantt vremenski plan (nacrt)',
		pageOf: (page, total) => `Stranica ${page} od ${total}`,
		weeks: 'Tjedni',
		task: 'Zadatak',
		offerTitleSuffix: 'Ponuda',
		offerVision: 'Vizija ponude',
		offerDetails: 'Detalji ponude',
		offeredPrice: 'Ponuđena cijena:',
		validUntil: 'Vrijedi do:',
		projectStart: 'Početak projekta:',
		timelineEstimate: 'Procjena vremena:',
		message: 'Poruka:',
		ganttTimelineFromOffer: 'Gantt vremenski plan (iz ponude)',
		projectOverview: 'Pregled projekta',
		status: 'Status:',
		budget: 'Budžet:',
		dueDate: 'Rok:',
		client: 'Klijent:',
		company: 'Tvrtka:',
		offerPrice: 'Cijena ponude:',
		gardenVision: 'Vizija vrta',
		proposedActualVision: 'Predložena / stvarna vizija',
		projectGantt: 'Gantt projekta',
		taskList: 'Popis zadataka',
		moreTasks: (count) => `(+${count} dodatnih zadataka)`,
		assignees: 'Dodijeljeno:',
		assigneesUnassigned: 'Dodijeljeno: Nije dodijeljeno',
		statusLabel: 'Status:',
		dependencies: 'Ovisnosti:',
		dependenciesNone: 'nema',
		progress: 'Napredak:',
		dateLocale: 'hr-HR'
	}
};

export function resolvePdfLanguage(language?: Language): Language {
	if (language && (language === 'en' || language === 'de' || language === 'hr')) {
		return language;
	}
	try {
		const stored = localStorage.getItem(STORAGE_KEY);
		if (stored === 'de' || stored === 'hr' || stored === 'en') {
			return stored;
		}
	} catch {
		// Ignore storage failures.
	}
	return 'en';
}

export function getPdfLabels(language?: Language): PdfLabels {
	return labelsByLanguage[resolvePdfLanguage(language)];
}

export function formatPdfDate(iso: string | null | undefined, language?: Language): string {
	if (!iso) return '';
	const d = new Date(iso);
	if (Number.isNaN(d.getTime())) return '';
	const locale = getPdfLabels(language).dateLocale;
	return d.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' });
}
